<template>
	<component :is="tag" ref="viewport" class="vlist_viewport" @scroll.passive="onScroll">
		<div class="vlist_canvas" :style="{ height: `${totalHeight}px` }">
			<component :is="rowTag" v-for="row in visibleRows" :key="row.key"
				class="vlist_row"
				:style="{ height: `${rowHeight}px`, transform: `translateY(${row.offset}px)` }">
				<slot name="row" :item="row.item" :index="row.index" />
			</component>
		</div>
	</component>
</template>

<script>
// Lista virtualizada de filas de ALTO FIJO: sólo monta las que entran en el
// viewport (más un colchón), así una lista de miles de filas no cuesta miles
// de nodos ni miles de listeners. El alto total lo sostiene un canvas vacío,
// de modo que la barra de scroll del navegador es la real.
//
// Uso:
//   <VirtualList tag="ul" row-tag="li" :items="rows" :row-height="36"
//                :row-key="(r) => r.id">
//     <template #row="{ item }">…contenido de la fila…</template>
//   </VirtualList>
//
// Requisito: todas las filas miden `rowHeight` (el componente lo aplica). Para
// filas de alto variable esta técnica no sirve — la posición se calcula por
// multiplicación, no midiendo.
const FALLBACK_VIEWPORT = 480;

export default {
	name: "VirtualList",

	props: {
		items: { type: Array, required: true },
		// Alto de CADA fila, en píxeles.
		rowHeight: { type: Number, required: true },
		// Filas extra arriba y abajo del viewport: absorben el scroll rápido
		// sin dejar huecos en blanco entre repintados.
		overscan: { type: Number, default: 6 },
		tag: { type: String, default: "div" },
		rowTag: { type: String, default: "div" },
		// Clave estable por fila. Sin ella se usa el índice, que sirve mientras
		// la lista no se reordene.
		rowKey: { type: Function, default: null },
	},

	data() {
		return { scrollTop: 0, viewportHeight: 0 };
	},

	computed: {
		totalHeight() {
			return this.items.length * this.rowHeight;
		},
		// Antes de medir (y en jsdom, que no hace layout) se asume un viewport
		// razonable: la lista se ve completa en los tests y en el primer
		// frame, en vez de quedar vacía hasta el `mounted`.
		usableHeight() {
			return this.viewportHeight || FALLBACK_VIEWPORT;
		},
		firstIndex() {
			return Math.max(0, Math.floor(this.scrollTop / this.rowHeight) - this.overscan);
		},
		lastIndex() {
			const visible = Math.ceil((this.scrollTop + this.usableHeight) / this.rowHeight);
			return Math.min(this.items.length, visible + this.overscan);
		},
		visibleRows() {
			const rows = [];
			for (let index = this.firstIndex; index < this.lastIndex; index += 1) {
				const item = this.items[index];
				rows.push({
					item,
					index,
					offset: index * this.rowHeight,
					key: this.rowKey ? this.rowKey(item, index) : index,
				});
			}
			return rows;
		},
	},

	watch: {
		// Lista nueva (un filtro, otro mes): si el scroll quedaba más abajo de
		// lo que la lista nueva mide, la ventana apuntaría al vacío.
		items() {
			const viewport = this.$refs.viewport;
			if (!viewport) return;
			const maxScroll = Math.max(0, this.totalHeight - this.usableHeight);
			if (viewport.scrollTop > maxScroll) viewport.scrollTop = 0;
			this.scrollTop = viewport.scrollTop;
		},
	},

	mounted() {
		this.measure();
		// El alto del viewport lo fija el layout del padre y puede cambiar sin
		// que cambie el tamaño de la ventana (un panel que se despliega).
		if (typeof ResizeObserver !== "undefined") {
			this.resizeObserver = new ResizeObserver(this.measure);
			this.resizeObserver.observe(this.$refs.viewport);
		}
	},

	beforeDestroy() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}
	},

	methods: {
		measure() {
			const viewport = this.$refs.viewport;
			if (viewport) this.viewportHeight = viewport.clientHeight;
		},
		onScroll(event) {
			this.scrollTop = event.target.scrollTop;
		},
	},
};
</script>

<style scoped lang="scss">
.vlist_viewport {
	overflow-y: auto;
	margin: 0;
	padding: 0;
	list-style: none;
}

.vlist_canvas {
	position: relative;
}

// Las filas se posicionan por transform desde el tope del canvas: sin
// reflow por fila y sin depender del orden del DOM.
.vlist_row {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	box-sizing: border-box;
}
</style>
